import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { PlugsConnected } from "@phosphor-icons/react";
import ConnectionTestReport from "./ConnectionTestReport";
import InboxAckSettings from "./InboxAckSettings";
import InboxHandlingSection from "./InboxHandlingSection";
import InboxSmtpSection from "./InboxSmtpSection";
import InboxUrgencySection from "./InboxUrgencySection";
import InboxEmergencySection from "./InboxEmergencySection";
import InboxSnippetsSection from "./InboxSnippetsSection";

/** Mailbox settings for the email-to-request inbox: IMAP connection, handling toggles, sender SMTP, urgency, templates. Dialog on /inbox, inline in Settings. */
export default function InboxSettings({ open = true, onClose, onSaved, inline = false }) {
  const [s, setS] = useState(null);
  const [options, setOptions] = useState(null);
  const [password, setPassword] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => {
    if (!open) return;
    API.get("/inbox/settings").then((r) => setS(r.data)).catch(() => setS(null));
    API.get("/inbox/settings/options").then((r) => setOptions(r.data)).catch(() => setOptions({}));
  }, [open]);
  if (!open || !s) return inline ? <div className="h-24 animate-pulse rounded-lg bg-muted/40" /> : null;
  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await API.post("/inbox/imap/diagnose", { host: s.host, port: s.port, ssl: s.ssl, user: s.user, folder: s.folder, password: password || undefined });
      setTestResult(data);
      if (data.ok) toast.success(data.message); else toast.error(data.message);
    } catch (e) { const t = formatApiError(e?.response?.data?.detail) || "Connection test failed"; setTestResult({ ok: false, steps: [], pending: [], server_response: t, message: t, duration_ms: 0 }); toast.error(t); }
    finally { setTesting(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/inbox/settings", { ...s, password: password || undefined, smtp_password: smtpPassword || undefined });
      setS(data); setPassword(""); setSmtpPassword(""); toast.success("Mailbox settings saved"); onSaved && onSaved(); onClose && onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save"); } finally { setSaving(false); }
  };
  const body = (
    <div className="space-y-5" data-testid="inbox-settings">
      <div className="space-y-3" data-testid="inbox-mailbox-section">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mailbox (IMAP)</p>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div><p className="text-sm font-medium">Pull emails from this mailbox</p><p className="text-xs text-muted-foreground">Unread emails are imported into the inbox{s.last_poll_at ? ` · last check ${new Date(s.last_poll_at).toLocaleString()}` : ""}.</p></div>
          <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="inbox-set-enabled" />
        </div>
        <div className="grid sm:grid-cols-[1fr_110px] gap-3">
          <div><Label>IMAP host</Label><Input value={s.host} onChange={(e) => setS({ ...s, host: e.target.value })} placeholder="imap.yourcompany.co.uk" data-testid="inbox-set-host" /></div>
          <div><Label>Port</Label><Input type="number" value={s.port} onChange={(e) => setS({ ...s, port: Number(e.target.value) })} data-testid="inbox-set-port" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Mailbox username / address</Label><Input value={s.user} onChange={(e) => setS({ ...s, user: e.target.value })} placeholder="maintenance@yourcompany.co.uk" data-testid="inbox-set-user" /></div>
          <div><Label>Password {s.has_password && <span className="text-xs text-muted-foreground">(saved — leave blank to keep)</span>}</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={s.has_password ? "••••••••" : "App password"} autoComplete="new-password" data-testid="inbox-set-password" /></div>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div><Label>Folder</Label><Input value={s.folder} onChange={(e) => setS({ ...s, folder: e.target.value })} data-testid="inbox-set-folder" /></div>
          <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={s.ssl} onCheckedChange={(v) => setS({ ...s, ssl: v })} data-testid="inbox-set-ssl" /> SSL/TLS (993)</label>
        </div>
        {testResult && <ConnectionTestReport report={testResult} testid="inbox-test-result" />}
        <div className="flex justify-between items-center gap-3">
          <p className="text-[11px] text-muted-foreground">Gmail: imap.gmail.com + app password · Microsoft 365: outlook.office365.com (IMAP enabled). Replies quoting a <span className="font-mono">JR-XXXXXXXX</span> or <span className="font-mono">EMG-XXXXXXXX</span> reference thread onto that request / emergency job automatically.</p>
          <Button type="button" variant="outline" size="sm" onClick={test} disabled={testing || !s.host || !s.user || (!password && !s.has_password)} data-testid="inbox-test-btn"><PlugsConnected size={14} className="mr-1" /> {testing ? "Connecting…" : "Test connection"}</Button>
        </div>
      </div>
      <InboxHandlingSection s={s} setS={setS} options={options} />
      <InboxSmtpSection s={s} setS={setS} smtpPassword={smtpPassword} setSmtpPassword={setSmtpPassword} />
      <InboxEmergencySection s={s} setS={setS} options={options} />
      <InboxUrgencySection s={s} setS={setS} options={options} />
      <InboxSnippetsSection />
      <InboxAckSettings s={s} setS={setS} />
      <div className="flex flex-wrap gap-2 justify-end sticky bottom-0 bg-background/95 backdrop-blur py-2 border-t border-border">
        <Button onClick={save} disabled={saving} data-testid="inbox-settings-save">{saving ? "Saving…" : "Save all mailbox settings"}</Button>
      </div>
    </div>
  );
  if (inline) return <div className="rounded-lg border border-border bg-card p-5">{body}</div>;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="inbox-settings-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Mailbox settings</DialogTitle></DialogHeader>
        {body}
        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
